/**
 * ReSTIR DI + SDF 可視性。
 * G-buffer から表面を取り、半解像度リザーバで点光源を選び、解析 SDF で点光源の影を出す。
 * シェードは sourceRadius を球として評価する。影のレイは今どおり中心へ出す。
 * 新規候補の目標 PDF には短い vis を入れ、勝者の正確な交差はシェードで 1 本取る。
 * プレーンはレイ–OBB、多角形はレイ–三角柱、ブレースはレイ–カプセル、残りはボクセル SDF。
 * 太陽光の近距離は SDF 可視、距離が遠いと CSM。
 * 点光源と ReSTIR GI は同じデノイザに通し、太陽光と空はフル解像度で合成する。
 */

import * as THREE from 'three';
import type { GraphicsQuality } from './graphics-quality';
import { timePass, type PassTimer } from './gpu-pass-timer';
import type { GBuffer } from './gbuffer';
import type { VoxelSdfGpu } from './voxel-sdf';
import type { AnalyticSdfGpu } from './analytic-sdf';
import { sdfTraceGlsl } from './sdf-trace';
import {
  CHEAP_SDF_MAX_STEPS,
  DEFAULT_RESTIR_CHEAP_CANDIDATE_VISIBILITY,
} from './restir-sampling';
import {
  DEFAULT_RESTIR_DENOISER_MODE,
  FIREFLY_LUMINANCE,
  RestirDenoiser,
  resolveRestirDenoiserMode,
  type RestirDenoiserDebugView,
  type RestirDenoiserMode,
} from './restir-denoiser';
import {
  GI_DISTANCE_AMBIENT,
  GI_DISTANCE_AMBIENT_MAX,
  GI_DISTANCE_AMBIENT_MIN,
  GI_DISTANCE_FADE_END,
  GI_DISTANCE_FADE_START,
  GI_NEAR_AMBIENT,
  GI_NEAR_AMBIENT_MAX,
  GI_NEAR_AMBIENT_MIN,
  GI_SKY_WHITE_MIX,
  clampGiSlider,
} from '../shared/restir-gi-sampling';
import {
  CSM_CASCADE_CAPACITY,
  SKY_GROUND,
  SKY_HORIZON,
  SKY_ZENITH,
  SUN_COLOR,
  SUN_DIRECTION,
  SUN_INTENSITY,
  type Environment,
} from './environment';
import {
  RESTIR_LIGHT_LIST_CAPACITY,
  remapPointLightIndices,
  type PointLightSource,
} from '../shared/point-light-budget';

export type { RestirDenoiserDebugView, RestirDenoiserMode } from './restir-denoiser';
export { DEFAULT_RESTIR_DENOISER_MODE, RESTIR_DENOISER_MODES, resolveRestirDenoiserMode } from './restir-denoiser';

/** ReSTIR DI の点光源ランバート拡散の倍率。鏡面は変えない。 */
export const DI_DIFFUSE_GAIN = 4;
/** 形状内の点光源を球として評価する半径（メートル）。影の endpoint 用 sourceRadius とは別。 */
export const DI_SPHERE_RADIUS = 0.1;

function fullscreenTriangle(): THREE.BufferGeometry {
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute(
    'position',
    new THREE.BufferAttribute(new Float32Array([-1, -1, 0, 3, -1, 0, -1, 3, 0]), 3)
  );
  return geometry;
}

const VERT = /* glsl */ `
out vec2 vUv;
void main() {
	vUv = position.xy * 0.5 + 0.5;
	gl_Position = vec4(position.xy, 0.0, 1.0);
}
`;

const COMMON = /* glsl */ `
const int LIGHT_CAPACITY = ${RESTIR_LIGHT_LIST_CAPACITY};
const float PI = 3.14159265359;
const float DIRECT_GAIN = 1.0;
const float DIFFUSE_GAIN = ${DI_DIFFUSE_GAIN.toFixed(1)};

uniform sampler2D depthMap;
uniform sampler2D albedoMetalMap;
uniform sampler2D normalRoughMap;
#ifndef RESTIR_COMPOSE
uniform sampler2D lightMap;
uniform sampler3D sdfMap;
uniform sampler2D planeMap;
uniform sampler2D triangleMap;
uniform sampler2D braceMap;
uniform sampler2D latticeOriginMap;
uniform float planeCount;
uniform float triangleCount;
uniform float braceCount;
#endif
uniform int lightCount;
uniform float sdfMaxSteps;
uniform float sunSdfShadows;
uniform float diSdfShadows;
uniform vec3 sdfOrigin;
uniform float sdfExtent;
uniform float sdfVoxelSize;
uniform mat4 projInverse;
uniform mat4 viewInverse;
uniform vec3 cameraPos;
uniform vec3 sunDirection;
uniform vec3 sunColor;
uniform float sunEnabled;
uniform float ambientEnabled;
uniform float giNearAmbient;
uniform float giDistanceAmbient;
uniform vec3 skyZenith;
uniform vec3 skyHorizon;
uniform vec3 skyGround;

float saturate(float x) { return clamp(x, 0.0, 1.0); }
float lightLuminance(vec3 c) { return dot(c, vec3(0.2126, 0.7152, 0.0722)); }
float pow2(float x) { return x * x; }
float pow4(float x) { float x2 = x * x; return x2 * x2; }

uint hash(uint x) {
	x += x << 10u;
	x ^= x >> 6u;
	x += x << 3u;
	x ^= x >> 11u;
	x += x << 15u;
	return x;
}

float rand(inout uint rng) {
	rng = hash(rng);
	return float(rng) * (1.0 / 4294967295.0);
}

#ifndef RESTIR_COMPOSE
vec4 lightPosRange(int i) {
	return texture(lightMap, vec2((float(i) + 0.5) / float(LIGHT_CAPACITY), 0.25));
}

vec3 lightColor(int i) {
	return texture(lightMap, vec2((float(i) + 0.5) / float(LIGHT_CAPACITY), 0.75)).rgb;
}

float lightSourceRadius(int i) {
	return texture(lightMap, vec2((float(i) + 0.5) / float(LIGHT_CAPACITY), 0.75)).a;
}
#endif

float sceneDepth(vec2 uv) {
	return texture(depthMap, uv).r;
}

vec3 worldFromDepth(vec2 uv) {
	float depth = sceneDepth(uv);
	vec4 ndc = vec4(uv * 2.0 - 1.0, depth * 2.0 - 1.0, 1.0);
	vec4 view = projInverse * ndc;
	view /= view.w;
	return (viewInverse * view).xyz;
}

float distanceAttenuation(float dist, float cutoff) {
	float falloff = 1.0 / max(dist * dist, 0.01);
	if (cutoff > 0.0) falloff *= pow2(saturate(1.0 - pow4(dist / cutoff)));
	return falloff;
}

vec3 F_Schlick(vec3 F0, float VdotH) {
	return F0 + (1.0 - F0) * pow(saturate(1.0 - VdotH), 5.0);
}

float D_GGX(float NdotH, float a) {
	float a2 = a * a;
	float d = NdotH * NdotH * (a2 - 1.0) + 1.0;
	return a2 / max(PI * d * d, 1e-6);
}

float V_SmithGGX(float NdotV, float NdotL, float a) {
	float a2 = a * a;
	float gv = NdotL * sqrt(max(NdotV * NdotV * (1.0 - a2) + a2, 0.0));
	float gl = NdotV * sqrt(max(NdotL * NdotL * (1.0 - a2) + a2, 0.0));
	return 0.5 / max(gv + gl, 1e-6);
}

struct Surface {
	vec3 albedo;
	vec3 normal;
	vec3 geoNormal;
	float roughness;
	float metalness;
};

vec3 worldDelta(vec3 toward, vec3 away, vec3 p) {
	vec3 a = toward - p;
	vec3 b = p - away;
	return dot(a, a) < dot(b, b) ? a : b;
}

// dFdx/dFdy は2三角形のフルスクリーン四角形の対角で不連続になる。
// 近接ポリゴンでは深度微分も荒れ、G-buffer 法線を誤って捨てて半分だけ暗くなる。
vec3 geoNormalFromDepth(vec2 uv, vec3 p) {
	ivec2 size = textureSize(depthMap, 0);
	vec2 texel = 1.0 / vec2(size);
	vec3 dpdx = worldDelta(worldFromDepth(uv + vec2(texel.x, 0.0)), worldFromDepth(uv - vec2(texel.x, 0.0)), p);
	vec3 dpdy = worldDelta(worldFromDepth(uv + vec2(0.0, texel.y)), worldFromDepth(uv - vec2(0.0, texel.y)), p);
	vec3 n = cross(dpdx, dpdy);
	if (dot(n, n) < 1e-12) n = vec3(0.0, 1.0, 0.0);
	else n = normalize(n);
	vec3 V = normalize(cameraPos - p);
	if (dot(n, V) < 0.0) n = -n;
	return n;
}

Surface surfaceFromGBuffer(vec2 uv, vec3 p) {
	vec4 albedoMetal = texture(albedoMetalMap, uv);
	vec4 normalRough = texture(normalRoughMap, uv);
	Surface s;
	s.albedo = albedoMetal.rgb;
	s.metalness = albedoMetal.a;
	s.roughness = max(normalRough.a, 0.045);
	s.geoNormal = geoNormalFromDepth(uv, p);
	vec3 packed = normalRough.xyz * 2.0 - 1.0;
	float packedLen = length(packed);
	if (packedLen > 0.4) {
		s.normal = packed / packedLen;
	} else {
		s.normal = s.geoNormal;
	}
	return s;
}

#ifndef RESTIR_COMPOSE
struct DirectSample {
	vec3 irradiance;
	vec3 specular;
};

// 中心への最近点は L が変わらないので、拡散は見かけの角半径で回り込ませる。
// 鏡面は反射レイに最も近い球面上の点（Karis）。半径 0 なら点光源と同じ。
float sphereWrappedNdotL(float NdotLcenter, float sinTheta) {
	return saturate((NdotLcenter + sinTheta) / (1.0 + sinTheta));
}

vec3 sphereSpecularToLight(vec3 toCenter, vec3 R, float radius) {
	vec3 centerToRay = dot(toCenter, R) * R - toCenter;
	float len = length(centerToRay);
	if (len < 1e-6) return toCenter;
	return toCenter + centerToRay * saturate(radius / len);
}

DirectSample evaluateDirectForView(int lightIndex, vec3 p, Surface s, vec3 viewPosition) {
	DirectSample e;
	e.irradiance = vec3(0.0);
	e.specular = vec3(0.0);
	if (lightIndex < 0 || lightIndex >= lightCount) return e;
	vec4 pr = lightPosRange(lightIndex);
	vec3 toCenter = pr.xyz - p;
	float dist = length(toCenter);
	if (dist >= pr.w || dist < 1e-4) return e;
	vec3 Lcenter = toCenter / dist;
	vec3 V = normalize(viewPosition - p);
	float NdotV = max(dot(s.normal, V), 0.0);
	float radius = lightSourceRadius(lightIndex) > 0.0
		? min(${DI_SPHERE_RADIUS.toFixed(2)}, dist * 0.999)
		: 0.0;
	float sinTheta = radius / dist;
	float NdotL = sphereWrappedNdotL(dot(s.normal, Lcenter), sinTheta);
	if (NdotL <= 0.0 || NdotV <= 0.0) return e;
	vec3 radiance = lightColor(lightIndex) * distanceAttenuation(dist, pr.w) * DIRECT_GAIN;
	e.irradiance = radiance * NdotL * DIFFUSE_GAIN;

	vec3 toLight = sphereSpecularToLight(toCenter, reflect(-V, s.normal), radius);
	float specDist = length(toLight);
	if (specDist < 1e-4) return e;
	vec3 L = toLight / specDist;
	float NdotLspec = max(dot(s.normal, L), 0.0);
	if (NdotLspec <= 0.0) return e;
	vec3 H = normalize(V + L);
	float NdotH = max(dot(s.normal, H), 0.0);
	float VdotH = max(dot(V, H), 0.0);
	float a = max(s.roughness * s.roughness, 1e-4);
	vec3 F0 = mix(vec3(0.04), s.albedo, s.metalness);
	float sphereNorm = a / max(a + sinTheta, 1e-4);
	e.specular = D_GGX(NdotH, a) * V_SmithGGX(NdotV, NdotLspec, a) * F_Schlick(F0, VdotH)
		* radiance * NdotLspec * (sphereNorm * sphereNorm);
	return e;
}

DirectSample evaluateDirect(int lightIndex, vec3 p, Surface s) {
	return evaluateDirectForView(lightIndex, p, s, cameraPos);
}

vec3 evaluateLightForView(int lightIndex, vec3 p, Surface s, vec3 viewPosition) {
	DirectSample e = evaluateDirectForView(lightIndex, p, s, viewPosition);
	vec3 diffuse = s.albedo * (1.0 - s.metalness) / PI;
	return e.irradiance * diffuse + e.specular;
}

vec3 evaluateLight(int lightIndex, vec3 p, Surface s) {
	return evaluateLightForView(lightIndex, p, s, cameraPos);
}
#endif

vec3 compactRadiance(vec3 c) {
	float lum = lightLuminance(c);
	if (!(lum == lum) || lum < 0.0) return vec3(0.0);
	if (lum > ${FIREFLY_LUMINANCE.toFixed(1)}) return c * (${FIREFLY_LUMINANCE.toFixed(1)} / lum);
	return c;
}

float compactWeight(float w) {
	if (!(w == w) || w < 0.0 || w > 1e6) return 0.0;
	return w;
}

vec3 evaluateSun(vec3 p, Surface s) {
	if (sunEnabled < 0.5) return vec3(0.0);
	vec3 L = normalize(-sunDirection);
	vec3 V = normalize(cameraPos - p);
	float NdotL = max(dot(s.normal, L), 0.0);
	float NdotV = max(dot(s.normal, V), 0.0);
	if (NdotL <= 0.0) return vec3(0.0);
	vec3 H = normalize(V + L);
	float NdotH = max(dot(s.normal, H), 0.0);
	float VdotH = max(dot(V, H), 0.0);
	float a = max(s.roughness * s.roughness, 1e-4);
	vec3 F0 = mix(vec3(0.04), s.albedo, s.metalness);
	vec3 specular = NdotV > 0.0
		? D_GGX(NdotH, a) * V_SmithGGX(max(NdotV, 1e-4), NdotL, a) * F_Schlick(F0, VdotH)
		: vec3(0.0);
	vec3 diffuse = s.albedo * (1.0 - s.metalness) / PI;
	return (diffuse + specular) * sunColor * NdotL;
}

vec3 skyGradient(vec3 dir) {
	vec3 d = normalize(dir);
	float h = d.y;
	vec3 sky = mix(skyHorizon, skyZenith, clamp(h * 0.85 + 0.15, 0.0, 1.0));
	vec3 below = mix(skyHorizon, skyGround, clamp(-h * 1.4, 0.0, 1.0));
	return mix(below, sky, step(0.0, h));
}

vec3 skyIrradianceColor(vec3 dir) {
	return mix(skyGradient(dir), vec3(1.0), ${GI_SKY_WHITE_MIX.toFixed(2)});
}

vec3 skyColor(vec3 dir) {
	vec3 color = skyGradient(dir);
	if (sunEnabled < 0.5) return color;
	vec3 d = normalize(dir);
	float sunDot = clamp(dot(d, -normalize(sunDirection)), 0.0, 1.0);
	float disc = smoothstep(0.9994, 0.99985, sunDot);
	float halo = pow(sunDot, 48.0) * 0.45;
	return color + sunColor * (disc * 2.2 + halo) / max(length(sunColor), 1e-4);
}

#ifndef RESTIR_COMPOSE
${sdfTraceGlsl()}

float traceSdfVisibility(vec3 origin, vec3 direction, float maxDistance, float maxSteps) {
	if (diSdfShadows < 0.5) return 1.0;
	return traceSdfAnyHit(origin, direction, maxDistance, maxSteps) < 0.0 ? 1.0 : 0.0;
}

float evaluateLightVisibility(int lightIndex, vec3 p, Surface s, float maxSteps) {
	vec4 pr = lightPosRange(lightIndex);
	vec3 toLight = pr.xyz - p;
	float dist = length(toLight);
	if (dist >= pr.w || dist < 1e-4) return 0.0;
	vec3 direction = toLight / dist;
	// 深度微分の geoNormal はカメラ向きに補正されており、斜め見で壁に沿って滑る。
	vec3 origin = p + s.normal * surfaceBias();
	float endpointBias = max(surfaceBias(), lightSourceRadius(lightIndex) + sdfVoxelSize);
	return traceSdfVisibility(origin, direction, max(dist - endpointBias, 0.0), maxSteps);
}

float evaluateLightTarget(int lightIndex, vec3 p, Surface s, float visSteps) {
	float unshadowed = lightLuminance(evaluateLight(lightIndex, p, s));
	if (unshadowed <= 0.0) return 0.0;
	if (visSteps <= 0.0) return unshadowed;
	return unshadowed * evaluateLightVisibility(lightIndex, p, s, visSteps);
}

float sampleSunSdfVisibility(vec3 p, vec3 n) {
	if (sunSdfShadows < 0.5) return 1.0;
	vec3 origin = p + n * surfaceBias();
	vec3 L = normalize(-sunDirection);
	return traceSdfAnyHit(origin, L, sdfExtent, sdfMaxSteps) < 0.0 ? 1.0 : 0.0;
}
#endif
`;

function reservoirFrag(): string {
  return /* glsl */ `
in vec2 vUv;
layout(location = 0) out vec4 reservoirOut;
layout(location = 1) out vec4 positionOut;
layout(location = 2) out vec4 normalOut;
layout(location = 3) out vec4 materialOut;

${COMMON}

uniform sampler2D previousReservoir;
uniform sampler2D previousPosition;
uniform sampler2D previousNormal;
uniform sampler2D lightIndexMap;
uniform mat4 previousViewProjection;
uniform vec2 reservoirTexel;
uniform float frame;
uniform float historyValid;
uniform float cheapCandidateVisibility;

const float CHEAP_SDF_MAX_STEPS = ${CHEAP_SDF_MAX_STEPS.toFixed(1)};
const float VIS_TAG_SHADE = 0.25;

void updateReservoir(
	inout int selected,
	inout float weightSum, inout float selectedTarget, inout float sampleCount,
	int candidate, float candidateWeight, float candidateTarget,
	float candidateCount, inout uint rng
) {
	if (candidateCount <= 0.0) return;
	sampleCount += candidateCount;
	if (candidateWeight <= 0.0) return;
	weightSum += candidateWeight;
	if (rand(rng) * weightSum < candidateWeight) {
		selected = candidate;
		selectedTarget = candidateTarget;
	}
}

bool validHistory(vec2 uv, vec3 p, vec3 n) {
	if (any(lessThanEqual(uv, vec2(0.0))) || any(greaterThanEqual(uv, vec2(1.0)))) return false;
	vec4 oldPosition = texture(previousPosition, uv);
	vec3 oldNormal = texture(previousNormal, uv).xyz;
	return oldPosition.w > 0.5 && distance(oldPosition.xyz, p) < 0.45 && dot(oldNormal, n) > 0.88;
}

void reuseReservoir(
	vec2 uv, vec3 p, Surface surface,
	inout int selected,
	inout float weightSum, inout float selectedTarget, inout float sampleCount,
	inout int sourceCount,
	inout uint rng
) {
	if (!validHistory(uv, p, surface.normal)) return;
	vec4 old = texture(previousReservoir, uv);
	if (old.w <= 0.0 || old.x < 0.0) return;
	int oldIndex = clamp(int(floor(old.x)), 0, LIGHT_CAPACITY - 1);
	float mapped = texture(lightIndexMap, vec2((float(oldIndex) + 0.5) / float(LIGHT_CAPACITY), 0.5)).r;
	if (mapped < 0.0) return;
	int candidate = clamp(int(floor(mapped)), 0, LIGHT_CAPACITY - 1);
	if (candidate >= lightCount) return;
	float target = evaluateLightTarget(candidate, p, surface, 0.0);
	float reusedCount = min(old.w, 20.0);
	float reusedWeight = target * old.y * reusedCount;
	sourceCount++;
	updateReservoir(selected, weightSum, selectedTarget, sampleCount,
		candidate, reusedWeight, target, reusedCount, rng);
}

void main() {
	float depth = sceneDepth(vUv);
	if (depth > 0.9998 || texture(albedoMetalMap, vUv).a < 0.0) {
		reservoirOut = vec4(-1.0, 0.0, 0.0, 0.0);
		positionOut = vec4(0.0);
		normalOut = vec4(0.0);
		materialOut = vec4(0.0);
		return;
	}

	vec3 p = worldFromDepth(vUv);
	Surface surface = surfaceFromGBuffer(vUv, p);
	vec3 V = normalize(cameraPos - p);
	if (dot(surface.normal, V) < 0.0) surface.normal = surface.geoNormal;
	if (dot(surface.normal, V) < 0.0) surface.normal = -surface.normal;

	if (lightCount <= 0) {
		reservoirOut = vec4(-1.0, 0.0, 0.0, 0.0);
		positionOut = vec4(p, 1.0);
		normalOut = vec4(surface.normal, surface.metalness);
		materialOut = vec4(surface.albedo, surface.roughness);
		return;
	}

	uint rng = uint(gl_FragCoord.x) * 1973u + uint(gl_FragCoord.y) * 9277u + uint(frame) * 26699u;
	int selected = -1;
	float weightSum = 0.0;
	float selectedTarget = 0.0;
	float sampleCount = 0.0;
	int sourceCount = 0;
	float countF = float(lightCount);
	float canonVisSteps = cheapCandidateVisibility > 0.5 ? CHEAP_SDF_MAX_STEPS : 0.0;

	for (int i = 0; i < 4; i++) {
		int candidate = min(int(rand(rng) * countF), lightCount - 1);
		float target = evaluateLightTarget(candidate, p, surface, canonVisSteps);
		updateReservoir(selected, weightSum, selectedTarget, sampleCount,
			candidate, target * countF, target, 1.0, rng);
	}

	if (historyValid > 0.5) {
		vec4 previousClip = previousViewProjection * vec4(p, 1.0);
		if (previousClip.w > 1e-4) {
			vec2 previousUv = previousClip.xy / previousClip.w * 0.5 + 0.5;
			reuseReservoir(previousUv, p, surface, selected,
				weightSum, selectedTarget, sampleCount, sourceCount, rng);
			float angle = rand(rng) * 2.0 * PI;
			for (int i = 0; i < 4; i++) {
				float a = angle + float(i) * 1.57079632679;
				float radius = mix(2.0, 12.0, rand(rng));
				vec2 offset = vec2(cos(a), sin(a)) * radius * reservoirTexel;
				reuseReservoir(previousUv + offset, p, surface, selected,
					weightSum, selectedTarget, sampleCount, sourceCount, rng);
			}
		}
	}
	if (sourceCount == 0) {
		for (int i = 0; i < 4; i++) {
			int candidate = min(int(rand(rng) * countF), lightCount - 1);
			float target = evaluateLightTarget(candidate, p, surface, canonVisSteps);
			updateReservoir(selected, weightSum, selectedTarget, sampleCount,
				candidate, target * countF, target, 1.0, rng);
		}
	}

	if (sampleCount > 64.0) {
		weightSum *= 64.0 / sampleCount;
		sampleCount = 64.0;
	}

	float finalWeight = 0.0;
	if (selected >= 0 && selectedTarget > 0.0) {
		finalWeight = weightSum / max(sampleCount * selectedTarget, 1e-8);
	}
	reservoirOut = selected >= 0
		? vec4(float(selected) + VIS_TAG_SHADE, min(finalWeight, 2048.0), selectedTarget, sampleCount)
		: vec4(-1.0, 0.0, 0.0, 0.0);
	positionOut = vec4(p, 1.0);
	normalOut = vec4(surface.normal, surface.metalness);
	materialOut = vec4(surface.albedo, surface.roughness);
}
`;
}

function shadeFrag(): string {
  return /* glsl */ `
in vec2 vUv;
layout(location = 0) out vec4 diffuseOut;
layout(location = 1) out vec4 specularOut;

${COMMON}

uniform sampler2D reservoirMap;
uniform sampler2D giIrradiance;

void main() {
	float depth = sceneDepth(vUv);
	if (depth > 0.9998 || texture(albedoMetalMap, vUv).a < 0.0) {
		diffuseOut = vec4(0.0);
		specularOut = vec4(0.0);
		return;
	}

	vec3 p = worldFromDepth(vUv);
	vec3 gi = vec3(0.0);
	if (ambientEnabled > 0.5) {
		float giFade = 1.0 - smoothstep(${GI_DISTANCE_FADE_START.toFixed(1)}, ${GI_DISTANCE_FADE_END.toFixed(1)}, length(p - cameraPos));
		gi = texture(giIrradiance, vUv).rgb * giFade;
	}

	vec3 irradiance = gi;
	vec3 specular = vec3(0.0);
	if (lightCount > 0) {
		Surface surface = surfaceFromGBuffer(vUv, p);
		vec3 V = normalize(cameraPos - p);
		if (dot(surface.normal, V) < 0.0) surface.normal = surface.geoNormal;
		if (dot(surface.normal, V) < 0.0) surface.normal = -surface.normal;

		vec4 reservoir = texture(reservoirMap, vUv);
		float weight = compactWeight(reservoir.y);
		if (reservoir.w > 0.0 && reservoir.x >= 0.0 && weight > 0.0) {
			int selected = clamp(int(floor(reservoir.x)), 0, LIGHT_CAPACITY - 1);
			DirectSample direct = evaluateDirect(selected, p, surface);
			float visibility = evaluateLightVisibility(selected, p, surface, sdfMaxSteps);
			float scale = weight * visibility;
			irradiance += compactRadiance(direct.irradiance * scale);
			specular = compactRadiance(direct.specular * scale);
		}
	}

	diffuseOut = vec4(irradiance, 1.0);
	specularOut = vec4(specular, 1.0);
}
`;
}

function sunVisFrag(): string {
  return /* glsl */ `
in vec2 vUv;
out vec4 fragColor;

${COMMON}

void main() {
	float depth = sceneDepth(vUv);
	if (depth > 0.9998 || texture(albedoMetalMap, vUv).a < 0.0) {
		fragColor = vec4(1.0);
		return;
	}
	vec3 p = worldFromDepth(vUv);
	Surface surface = surfaceFromGBuffer(vUv, p);
	vec3 V = normalize(cameraPos - p);
	if (dot(surface.normal, V) < 0.0) surface.normal = surface.geoNormal;
	if (dot(surface.normal, V) < 0.0) surface.normal = -surface.normal;
	fragColor = vec4(sampleSunSdfVisibility(p, surface.normal));
}
`;
}

function composeFrag(): string {
  return /* glsl */ `
#define RESTIR_COMPOSE
in vec2 vUv;
out vec4 fragColor;

${COMMON}

uniform sampler2D currentPosition;
uniform sampler2D currentNormal;
uniform sampler2D denoisedDiffuse;
uniform sampler2D denoisedSpecular;
uniform sampler2D sunSdfShadow;
uniform vec2 restirTexel;
uniform int debugView;
uniform sampler2D sunShadowMap0;
uniform sampler2D sunShadowMap1;
uniform sampler2D sunShadowMap2;
uniform mat4 sunShadowMatrix0;
uniform mat4 sunShadowMatrix1;
uniform mat4 sunShadowMatrix2;
uniform vec2 sunCsmCascades[3];
uniform mat4 cameraViewMatrix;
uniform float sunShadowsEnabled;
uniform float sunShadowCascadeCount;
uniform float sunShadowBias;
uniform float sunShadowNormalBias;
uniform float sunShadowRadius;
uniform vec2 sunShadowMapSize;
uniform float cameraNear;
uniform float shadowFar;

vec3 bilateralUpsample(sampler2D map, vec3 p, vec3 n, float roughness) {
	vec2 centerPix = floor(vUv / restirTexel);
	vec3 sum = vec3(0.0);
	float wSum = 0.0;
	float viewZ = length(p - cameraPos);
	float posLimit = max(sdfVoxelSize * 2.0, viewZ * 0.02);
	for (int y = -1; y <= 1; y++) {
		for (int x = -1; x <= 1; x++) {
			vec2 uv = (centerPix + vec2(float(x), float(y)) + 0.5) * restirTexel;
			if (any(lessThanEqual(uv, vec2(0.0))) || any(greaterThanEqual(uv, vec2(1.0)))) continue;
			vec4 tapPos = texture(currentPosition, uv);
			if (tapPos.w < 0.5) continue;
			vec3 tapN = texture(currentNormal, uv).xyz;
			float w = exp(-distance(p, tapPos.xyz) / posLimit);
			w *= pow(max(dot(n, tapN), 0.0), 16.0);
			w *= exp(-abs(roughness - texture(normalRoughMap, uv).a) * 6.0);
			sum += texture(map, uv).rgb * w;
			wSum += w;
		}
	}
	if (wSum < 1e-5) return texture(map, vUv).rgb;
	return sum / wSum;
}

const vec4 UNPACK_FACTORS4 = vec4(
	255.0 / 256.0,
	255.0 / 256.0 / 256.0,
	255.0 / 256.0 / 65536.0,
	1.0 / 16777216.0
);

float unpackRGBAToDepth(vec4 v) {
	return dot(v, UNPACK_FACTORS4);
}

float textureShadowCompare(sampler2D depths, vec2 uv, float compare) {
	return step(compare, unpackRGBAToDepth(texture(depths, uv)));
}

float sampleShadowMap(sampler2D shadowMap, mat4 shadowMatrix, vec3 worldPos) {
	vec4 shadowCoord = shadowMatrix * vec4(worldPos, 1.0);
	shadowCoord.xyz /= shadowCoord.w;
	shadowCoord.z += sunShadowBias;
	bool inFrustum = shadowCoord.x >= 0.0 && shadowCoord.x <= 1.0 && shadowCoord.y >= 0.0 && shadowCoord.y <= 1.0;
	if (!inFrustum || shadowCoord.z > 1.0) return 1.0;
	if (sunShadowRadius < 1.05) {
		return textureShadowCompare(shadowMap, shadowCoord.xy, shadowCoord.z);
	}
	vec2 texelSize = sunShadowRadius / sunShadowMapSize;
	float sum = 0.0;
	for (int y = -1; y <= 1; y++) {
		for (int x = -1; x <= 1; x++) {
			sum += textureShadowCompare(
				shadowMap,
				shadowCoord.xy + vec2(float(x), float(y)) * texelSize,
				shadowCoord.z
			);
		}
	}
	return sum / 9.0;
}

float sampleCascade(int index, vec3 worldPos) {
	if (index == 0) return sampleShadowMap(sunShadowMap0, sunShadowMatrix0, worldPos);
	if (index == 1) return sampleShadowMap(sunShadowMap1, sunShadowMatrix1, worldPos);
	return sampleShadowMap(sunShadowMap2, sunShadowMatrix2, worldPos);
}

float sampleSunShadow(vec3 p, Surface s) {
	if (sunShadowsEnabled < 0.5 || sunShadowCascadeCount < 0.5) return 1.0;
	vec3 worldPos = p + s.geoNormal * sunShadowNormalBias;
	float viewZ = -(cameraViewMatrix * vec4(p, 1.0)).z;
	float linearDepth = viewZ / max(shadowFar - cameraNear, 1e-4);
	int count = int(sunShadowCascadeCount + 0.5);
	for (int i = 0; i < 3; i++) {
		if (i >= count) break;
		vec2 cascade = sunCsmCascades[i];
		bool last = i == count - 1;
		if (linearDepth >= cascade.x && (linearDepth < cascade.y || last)) {
			float csm = sampleCascade(i, worldPos);
			if (i == 0 && sunSdfShadows > 0.5) {
				float sdfVis = texture(sunSdfShadow, vUv).r;
				float fadeEnd = max(sdfExtent * 0.5 - 2.0, 1.0);
				float cover = 1.0 - smoothstep(fadeEnd * 0.65, fadeEnd, length(p - cameraPos));
				return mix(csm, sdfVis, cover);
			}
			return csm;
		}
	}
	return 1.0;
}

vec3 debugColor(vec3 p, vec3 n, float roughness) {
	if (debugView == 3) {
		vec2 var = bilateralUpsample(denoisedDiffuse, p, n, roughness).rg;
		return vec3(sqrt(var.x), sqrt(var.y), 0.0) * 4.0;
	}
	vec3 irr = bilateralUpsample(denoisedDiffuse, p, n, roughness);
	vec3 spec = bilateralUpsample(denoisedSpecular, p, n, roughness);
	vec4 albedoMetal = texture(albedoMetalMap, vUv);
	return irr * albedoMetal.rgb * (1.0 - max(albedoMetal.a, 0.0)) / PI + spec;
}

void main() {
	float depth = sceneDepth(vUv);
	if (depth > 0.9998) {
		vec3 dir = normalize(worldFromDepth(vUv) - cameraPos);
		fragColor = vec4(skyColor(dir), 1.0);
		return;
	}

	vec3 p = worldFromDepth(vUv);
	vec4 albedoMetal = texture(albedoMetalMap, vUv);
	if (albedoMetal.a < 0.0) {
		fragColor = vec4(albedoMetal.rgb, 1.0);
		return;
	}

	Surface surface = surfaceFromGBuffer(vUv, p);
	vec3 V = normalize(cameraPos - p);
	if (dot(surface.normal, V) < 0.0) surface.normal = surface.geoNormal;
	if (dot(surface.normal, V) < 0.0) surface.normal = -surface.normal;

	vec3 pointLit = debugColor(p, surface.geoNormal, surface.roughness);
	if (debugView > 0) {
		fragColor = vec4(pointLit, 1.0);
		return;
	}

	vec3 color = vec3(0.0);
	if (ambientEnabled > 0.5) {
		float giFade = 1.0 - smoothstep(${GI_DISTANCE_FADE_START.toFixed(1)}, ${GI_DISTANCE_FADE_END.toFixed(1)}, length(p - cameraPos));
		float envFade = 1.0 - giFade;
		vec3 env = skyIrradianceColor(surface.geoNormal) * PI * mix(giNearAmbient, giDistanceAmbient, envFade);
		color += env * albedoMetal.rgb * (1.0 - max(albedoMetal.a, 0.0)) / PI;
	}
	if (sunEnabled > 0.5) {
		color += evaluateSun(p, surface) * sampleSunShadow(p, surface);
	}
	color += pointLit;
	fragColor = vec4(color, 1.0);
}
`;
}

const DEBUG_VIEW_ID: Record<RestirDenoiserDebugView, number> = {
  off: 0,
  raw: 1,
  temporal: 2,
  variance: 3,
  denoised: 4,
};

export class RestirSdfLighting {
  private readonly renderer: THREE.WebGLRenderer;
  private readonly denoiser: RestirDenoiser;
  private readonly lightData = new Float32Array(RESTIR_LIGHT_LIST_CAPACITY * 2 * 4);
  private readonly lightTexture: THREE.DataTexture;
  private readonly lightIndexData = new Float32Array(RESTIR_LIGHT_LIST_CAPACITY * 4);
  private readonly lightIndexMap: THREE.DataTexture;
  private readonly linearColor = new THREE.Color();
  private readonly passCamera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
  private readonly reservoirScene = new THREE.Scene();
  private readonly shadeScene = new THREE.Scene();
  private readonly composeScene = new THREE.Scene();
  private readonly sunVisScene = new THREE.Scene();
  private readonly reservoirMaterial: THREE.ShaderMaterial;
  private readonly shadeMaterial: THREE.ShaderMaterial;
  private readonly composeMaterial: THREE.ShaderMaterial;
  private readonly sunVisMaterial: THREE.ShaderMaterial;
  private readonly reservoirs: THREE.WebGLRenderTarget[];
  private readonly noisy: THREE.WebGLRenderTarget;
  private readonly sunVisTarget: THREE.WebGLRenderTarget;
  private readonly quadGeometry: THREE.BufferGeometry;
  private readonly previousViewProjection = new THREE.Matrix4();
  private readonly currentViewProjection = new THREE.Matrix4();
  private previousLights: PointLightSource[] = [];
  private readIndex = 0;
  private frame = 0;
  private historyValid = false;
  private _denoiserMode: RestirDenoiserMode = DEFAULT_RESTIR_DENOISER_MODE;
  private _cheapCandidateVisibility = DEFAULT_RESTIR_CHEAP_CANDIDATE_VISIBILITY;
  private _sunSdfShadowsEnabled = true;
  private _diSdfShadowsEnabled = true;
  private _analyticSdfEnabled = true;
  private _analyticBvhEnabled = true;
  private _voxelSpheresEnabled = true;
  private _sunEnabled = true;
  private _ambientEnabled = true;
  private readonly dummyShadowMap: THREE.DataTexture;
  private readonly dummyGiMap: THREE.DataTexture;
  private _lightCount = 0;
  private readonly sunCsmCascades = [
    new THREE.Vector2(),
    new THREE.Vector2(),
    new THREE.Vector2(),
  ];
  scale = 0.5;

  get denoiserMode(): RestirDenoiserMode {
    return this._denoiserMode;
  }

  set denoiserMode(value: RestirDenoiserMode) {
    const next = resolveRestirDenoiserMode(value);
    if (this._denoiserMode === next) return;
    this._denoiserMode = next;
    this.denoiser.mode = next;
    this.resetHistory();
  }

  get cheapCandidateVisibility(): boolean {
    return this._cheapCandidateVisibility;
  }

  set cheapCandidateVisibility(value: boolean) {
    if (this._cheapCandidateVisibility === value) return;
    this._cheapCandidateVisibility = value;
    this.reservoirMaterial.uniforms.cheapCandidateVisibility.value = value ? 1 : 0;
    this.resetHistory();
  }

  get sunSdfShadowsEnabled(): boolean {
    return this._sunSdfShadowsEnabled;
  }

  set sunSdfShadowsEnabled(value: boolean) {
    if (this._sunSdfShadowsEnabled === value) return;
    this._sunSdfShadowsEnabled = value;
    this.setUniform('sunSdfShadows', value ? 1 : 0);
  }

  get diSdfShadowsEnabled(): boolean {
    return this._diSdfShadowsEnabled;
  }

  set diSdfShadowsEnabled(value: boolean) {
    if (this._diSdfShadowsEnabled === value) return;
    this._diSdfShadowsEnabled = value;
    this.setUniform('diSdfShadows', value ? 1 : 0);
    this.resetHistory();
  }

  get analyticSdfEnabled(): boolean {
    return this._analyticSdfEnabled;
  }

  set analyticSdfEnabled(value: boolean) {
    if (this._analyticSdfEnabled === value) return;
    this._analyticSdfEnabled = value;
    this.setUniform('analyticSdf', value ? 1 : 0);
    this.resetHistory();
  }

  get analyticBvhEnabled(): boolean {
    return this._analyticBvhEnabled;
  }

  set analyticBvhEnabled(value: boolean) {
    if (this._analyticBvhEnabled === value) return;
    this._analyticBvhEnabled = value;
    this.setUniform('analyticBvh', value ? 1 : 0);
    this.resetHistory();
  }

  get voxelSpheresEnabled(): boolean {
    return this._voxelSpheresEnabled;
  }

  set voxelSpheresEnabled(value: boolean) {
    if (this._voxelSpheresEnabled === value) return;
    this._voxelSpheresEnabled = value;
    this.setUniform('voxelSpheres', value ? 1 : 0);
    this.resetHistory();
  }

  get sunEnabled(): boolean {
    return this._sunEnabled;
  }

  set sunEnabled(value: boolean) {
    if (this._sunEnabled === value) return;
    this._sunEnabled = value;
    this.setUniform('sunEnabled', value ? 1 : 0);
  }

  get ambientEnabled(): boolean {
    return this._ambientEnabled;
  }

  set ambientEnabled(value: boolean) {
    if (this._ambientEnabled === value) return;
    this._ambientEnabled = value;
    this.setUniform('ambientEnabled', value ? 1 : 0);
  }

  private _nearAmbient = GI_NEAR_AMBIENT;
  private _distanceAmbient = GI_DISTANCE_AMBIENT;

  get nearAmbient(): number {
    return this._nearAmbient;
  }

  set nearAmbient(value: number) {
    const next = clampGiSlider(value, GI_NEAR_AMBIENT_MIN, GI_NEAR_AMBIENT_MAX, GI_NEAR_AMBIENT);
    this._nearAmbient = next;
    this.setUniform('giNearAmbient', next);
  }

  get distanceAmbient(): number {
    return this._distanceAmbient;
  }

  set distanceAmbient(value: number) {
    const next = clampGiSlider(
      value,
      GI_DISTANCE_AMBIENT_MIN,
      GI_DISTANCE_AMBIENT_MAX,
      GI_DISTANCE_AMBIENT
    );
    this._distanceAmbient = next;
    this.setUniform('giDistanceAmbient', next);
  }

  constructor(renderer: THREE.WebGLRenderer) {
    this.renderer = renderer;
    this.denoiser = new RestirDenoiser(renderer);
    this.lightTexture = new THREE.DataTexture(
      this.lightData,
      RESTIR_LIGHT_LIST_CAPACITY,
      2,
      THREE.RGBAFormat,
      THREE.FloatType
    );
    this.lightTexture.magFilter = THREE.NearestFilter;
    this.lightTexture.minFilter = THREE.NearestFilter;
    this.lightTexture.wrapS = THREE.ClampToEdgeWrapping;
    this.lightTexture.wrapT = THREE.ClampToEdgeWrapping;
    this.lightTexture.generateMipmaps = false;
    this.lightTexture.flipY = false;
    this.lightTexture.needsUpdate = true;

    this.lightIndexData.fill(-1);
    this.lightIndexMap = new THREE.DataTexture(
      this.lightIndexData,
      RESTIR_LIGHT_LIST_CAPACITY,
      1,
      THREE.RGBAFormat,
      THREE.FloatType
    );
    this.lightIndexMap.magFilter = THREE.NearestFilter;
    this.lightIndexMap.minFilter = THREE.NearestFilter;
    this.lightIndexMap.wrapS = THREE.ClampToEdgeWrapping;
    this.lightIndexMap.wrapT = THREE.ClampToEdgeWrapping;
    this.lightIndexMap.generateMipmaps = false;
    this.lightIndexMap.flipY = false;
    this.lightIndexMap.needsUpdate = true;

    const shared = this.makeSharedUniforms();
    this.reservoirMaterial = new THREE.ShaderMaterial({
      glslVersion: THREE.GLSL3,
      depthTest: false,
      depthWrite: false,
      toneMapped: false,
      uniforms: {
        ...shared,
        previousReservoir: { value: null },
        previousPosition: { value: null },
        previousNormal: { value: null },
        lightIndexMap: { value: this.lightIndexMap },
        previousViewProjection: { value: new THREE.Matrix4() },
        reservoirTexel: { value: new THREE.Vector2(1, 1) },
        frame: { value: 0 },
        historyValid: { value: 0 },
        cheapCandidateVisibility: { value: DEFAULT_RESTIR_CHEAP_CANDIDATE_VISIBILITY ? 1 : 0 },
      },
      vertexShader: VERT,
      fragmentShader: reservoirFrag(),
    });
    this.shadeMaterial = new THREE.ShaderMaterial({
      glslVersion: THREE.GLSL3,
      depthTest: false,
      depthWrite: false,
      toneMapped: false,
      uniforms: {
        ...this.makeSharedUniforms(),
        reservoirMap: { value: null },
        giIrradiance: { value: null },
      },
      vertexShader: VERT,
      fragmentShader: shadeFrag(),
    });
    this.composeMaterial = new THREE.ShaderMaterial({
      glslVersion: THREE.GLSL3,
      depthTest: false,
      depthWrite: false,
      toneMapped: false,
      uniforms: {
        ...this.makeSharedUniforms(),
        currentPosition: { value: null },
        currentNormal: { value: null },
        denoisedDiffuse: { value: null },
        denoisedSpecular: { value: null },
        sunSdfShadow: { value: null },
        restirTexel: { value: new THREE.Vector2(1, 1) },
        debugView: { value: 0 },
        sunShadowMap0: { value: null },
        sunShadowMap1: { value: null },
        sunShadowMap2: { value: null },
        sunShadowMatrix0: { value: new THREE.Matrix4() },
        sunShadowMatrix1: { value: new THREE.Matrix4() },
        sunShadowMatrix2: { value: new THREE.Matrix4() },
        sunCsmCascades: { value: this.sunCsmCascades },
        cameraViewMatrix: { value: new THREE.Matrix4() },
        sunShadowsEnabled: { value: 1 },
        sunShadowCascadeCount: { value: CSM_CASCADE_CAPACITY },
        sunShadowBias: { value: -0.0002 },
        sunShadowNormalBias: { value: 0.035 },
        sunShadowRadius: { value: 1.5 },
        sunShadowMapSize: { value: new THREE.Vector2(2048, 2048) },
        cameraNear: { value: 0.05 },
        shadowFar: { value: 420 },
      },
      vertexShader: VERT,
      fragmentShader: composeFrag(),
    });
    this.sunVisMaterial = new THREE.ShaderMaterial({
      glslVersion: THREE.GLSL3,
      depthTest: false,
      depthWrite: false,
      toneMapped: false,
      uniforms: this.makeSharedUniforms(),
      vertexShader: VERT,
      fragmentShader: sunVisFrag(),
    });

    this.quadGeometry = fullscreenTriangle();
    this.reservoirScene.add(new THREE.Mesh(this.quadGeometry, this.reservoirMaterial));
    this.shadeScene.add(new THREE.Mesh(this.quadGeometry, this.shadeMaterial));
    this.composeScene.add(new THREE.Mesh(this.quadGeometry, this.composeMaterial));
    this.sunVisScene.add(new THREE.Mesh(this.quadGeometry, this.sunVisMaterial));
    this.reservoirs = [this.makeReservoirTarget(), this.makeReservoirTarget()];
    this.noisy = this.makeNoisyTarget();
    this.sunVisTarget = new THREE.WebGLRenderTarget(1, 1, {
      minFilter: THREE.NearestFilter,
      magFilter: THREE.NearestFilter,
      depthBuffer: false,
    });
    this.sunVisTarget.texture.colorSpace = THREE.NoColorSpace;
    this.dummyShadowMap = new THREE.DataTexture(new Uint8Array([255, 255, 255, 255]), 1, 1);
    this.dummyShadowMap.colorSpace = THREE.NoColorSpace;
    this.dummyShadowMap.needsUpdate = true;
    this.dummyGiMap = new THREE.DataTexture(new Uint8Array([0, 0, 0, 0]), 1, 1);
    this.dummyGiMap.colorSpace = THREE.NoColorSpace;
    this.dummyGiMap.needsUpdate = true;
  }

  applyQuality(quality: GraphicsQuality): void {
    this.scale = quality.restirScale;
    this.setUniform('sdfMaxSteps', quality.sdfMaxSteps);
    this.denoiser.applyQuality(quality);
    this.resetHistory();
  }

  setDenoiserDebugView(view: RestirDenoiserDebugView): void {
    this.denoiser.debugView = view;
    this.composeMaterial.uniforms.debugView.value = DEBUG_VIEW_ID[view];
  }

  getDenoiserTimings(): { temporal: number; variance: number; atrous: number; total: number } {
    return { ...this.denoiser.lastCpuMs };
  }

  setLights(lights: readonly PointLightSource[]): void {
    const nextLights = lights.slice(0, RESTIR_LIGHT_LIST_CAPACITY);
    const remap = remapPointLightIndices(this.previousLights, nextLights);
    this.lightIndexData.fill(-1);
    for (let i = 0; i < RESTIR_LIGHT_LIST_CAPACITY; i++) {
      this.lightIndexData[i * 4] = remap[i] < 0 ? -1 : remap[i] + 0.5;
    }
    this.lightIndexMap.needsUpdate = true;
    this.previousLights = nextLights.map((source) => ({ ...source }));
    this.lightData.fill(0);
    const count = nextLights.length;
    for (let i = 0; i < count; i++) {
      const source = nextLights[i];
      const positionOffset = i * 4;
      const colorOffset = (RESTIR_LIGHT_LIST_CAPACITY + i) * 4;
      this.lightData[positionOffset] = source.x;
      this.lightData[positionOffset + 1] = source.y;
      this.lightData[positionOffset + 2] = source.z;
      this.lightData[positionOffset + 3] = source.range;
      this.linearColor.setHex(source.color).convertSRGBToLinear();
      this.lightData[colorOffset] = this.linearColor.r * source.intensity;
      this.lightData[colorOffset + 1] = this.linearColor.g * source.intensity;
      this.lightData[colorOffset + 2] = this.linearColor.b * source.intensity;
      this.lightData[colorOffset + 3] = source.sourceRadius;
    }
    this.lightTexture.needsUpdate = true;
    this._lightCount = count;
    this.setUniform('lightCount', count);
  }

  get lightMap(): THREE.Texture {
    return this.lightTexture;
  }

  get lightCount(): number {
    return this._lightCount;
  }

  setSize(width: number, height: number): void {
    const w = Math.max(1, Math.floor(width * this.scale));
    const h = Math.max(1, Math.floor(height * this.scale));
    for (const target of this.reservoirs) target.setSize(w, h);
    this.noisy.setSize(w, h);
    this.sunVisTarget.setSize(Math.max(1, width), Math.max(1, height));
    this.denoiser.setSize(w, h);
    this.reservoirMaterial.uniforms.reservoirTexel.value.set(1 / w, 1 / h);
    this.composeMaterial.uniforms.restirTexel.value.set(1 / w, 1 / h);
    this.resetHistory();
  }

  resetHistory(): void {
    this.historyValid = false;
    this.frame = 0;
    this.denoiser.resetHistory();
  }

  render(
    camera: THREE.PerspectiveCamera,
    gbuffer: GBuffer,
    sdf: VoxelSdfGpu,
    analytic: AnalyticSdfGpu,
    target: THREE.WebGLRenderTarget,
    environment: Environment,
    giIrradiance: THREE.Texture | null = null,
    timer: PassTimer | null = null
  ): void {
    this.frame++;
    camera.updateMatrixWorld();
    this.currentViewProjection.multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse);

    this.bindScene(gbuffer, sdf, analytic, camera);
    this.bindSunShadows(environment, camera);
    const writeIndex = 1 - this.readIndex;
    const previous = this.reservoirs[this.readIndex];
    const current = this.reservoirs[writeIndex];
    const ru = this.reservoirMaterial.uniforms;
    ru.previousReservoir.value = previous.textures[0];
    ru.previousPosition.value = previous.textures[1];
    ru.previousNormal.value = previous.textures[2];
    ru.previousViewProjection.value.copy(this.previousViewProjection);
    ru.frame.value = this.frame;
    ru.historyValid.value = this.historyValid ? 1 : 0;

    const previousAutoClear = this.renderer.autoClear;
    this.renderer.autoClear = true;
    this.renderer.setRenderTarget(current);
    timePass(timer, 'diReservoir', () => {
      this.renderer.render(this.reservoirScene, this.passCamera);
    });

    this.shadeMaterial.uniforms.reservoirMap.value = current.textures[0];
    this.shadeMaterial.uniforms.giIrradiance.value = giIrradiance ?? this.dummyGiMap;
    this.renderer.setRenderTarget(this.noisy);
    timePass(timer, 'diShade', () => {
      this.renderer.render(this.shadeScene, this.passCamera);
    });

    const sdfVoxelSize = sdf.extent / sdf.resolution;
    if (this._denoiserMode !== 'off') {
      timePass(timer, 'denoiser', () => {
        this.denoiser.render({
          camera,
          gbuffer,
          currentReservoir: current,
          previousReservoir: previous,
          previousViewProjection: this.previousViewProjection,
          noisyDiffuse: this.noisy.textures[0],
          noisySpecular: this.noisy.textures[1],
          sdfVoxelSize,
          historyValid: this.historyValid,
        });
      });
    }

    const cu = this.composeMaterial.uniforms;
    const debugView = cu.debugView.value as number;
    const denoisedDiffuse = this._denoiserMode !== 'off' ? this.denoiser.diffuse : this.noisy.textures[0];
    const denoisedSpecular = this._denoiserMode !== 'off' ? this.denoiser.specular : this.noisy.textures[1];
    cu.currentPosition.value = current.textures[1];
    cu.currentNormal.value = current.textures[2];
    if (debugView === 1) {
      cu.denoisedDiffuse.value = this.noisy.textures[0];
      cu.denoisedSpecular.value = this.noisy.textures[1];
    } else if (debugView === 2) {
      cu.denoisedDiffuse.value =
        this._denoiserMode !== 'off' ? this.denoiser.temporalDiffuse : this.noisy.textures[0];
      cu.denoisedSpecular.value =
        this._denoiserMode !== 'off' ? this.denoiser.temporalSpecular : this.noisy.textures[1];
    } else if (debugView === 3) {
      cu.denoisedDiffuse.value = this._denoiserMode !== 'off' ? this.denoiser.variance : this.noisy.textures[0];
      cu.denoisedSpecular.value = denoisedSpecular;
    } else {
      cu.denoisedDiffuse.value = denoisedDiffuse;
      cu.denoisedSpecular.value = denoisedSpecular;
    }
    if (this._sunSdfShadowsEnabled) {
      this.renderer.setRenderTarget(this.sunVisTarget);
      timePass(timer, 'sunVis', () => {
        this.renderer.render(this.sunVisScene, this.passCamera);
      });
      cu.sunSdfShadow.value = this.sunVisTarget.texture;
    }
    this.renderer.setRenderTarget(target);
    timePass(timer, 'compose', () => {
      this.renderer.render(this.composeScene, this.passCamera);
    });
    this.renderer.autoClear = previousAutoClear;

    this.readIndex = writeIndex;
    this.previousViewProjection.copy(this.currentViewProjection);
    this.historyValid = true;
  }

  dispose(): void {
    for (const target of this.reservoirs) target.dispose();
    this.noisy.dispose();
    this.denoiser.dispose();
    this.lightTexture.dispose();
    this.lightIndexMap.dispose();
    this.reservoirMaterial.dispose();
    this.shadeMaterial.dispose();
    this.composeMaterial.dispose();
    this.sunVisMaterial.dispose();
    this.sunVisTarget.dispose();
    this.quadGeometry.dispose();
    this.dummyShadowMap.dispose();
    this.dummyGiMap.dispose();
  }

  private bindScene(
    gbuffer: GBuffer,
    sdf: VoxelSdfGpu,
    analytic: AnalyticSdfGpu,
    camera: THREE.PerspectiveCamera
  ): void {
    for (const material of [
      this.reservoirMaterial,
      this.shadeMaterial,
      this.composeMaterial,
      this.sunVisMaterial,
    ]) {
      const u = material.uniforms;
      u.depthMap.value = gbuffer.depth;
      u.albedoMetalMap.value = gbuffer.albedoMetal;
      u.normalRoughMap.value = gbuffer.normalRough;
      u.sdfMap.value = sdf.texture;
      u.sdfOrigin.value.copy(sdf.origin);
      u.sdfExtent.value = sdf.extent;
      u.sdfVoxelSize.value = sdf.extent / sdf.resolution;
      u.planeMap.value = analytic.planes;
      u.triangleMap.value = analytic.triangles;
      u.braceMap.value = analytic.braces;
      u.latticeOriginMap.value = analytic.originMap;
      u.planeCount.value = analytic.planeCount;
      u.triangleCount.value = analytic.triangleCount;
      u.braceCount.value = analytic.braceCount;
      u.projInverse.value.copy(camera.projectionMatrixInverse);
      u.viewInverse.value.copy(camera.matrixWorld);
      u.cameraPos.value.setFromMatrixPosition(camera.matrixWorld);
      u.sunDirection.value.copy(SUN_DIRECTION);
      u.sunColor.value.copy(SUN_COLOR).multiplyScalar(SUN_INTENSITY);
      u.skyZenith.value.copy(SKY_ZENITH);
      u.skyHorizon.value.copy(SKY_HORIZON);
      u.skyGround.value.copy(SKY_GROUND);
    }
  }

  private bindSunShadows(environment: Environment, camera: THREE.PerspectiveCamera): void {
    const u = this.composeMaterial.uniforms;
    const enabled = environment.shouldUpdateShadows;
    const count = environment.activeShadowCascades;
    u.sunShadowsEnabled.value = enabled ? 1 : 0;
    u.sunShadowCascadeCount.value = count;
    u.cameraViewMatrix.value.copy(camera.matrixWorldInverse);
    u.cameraNear.value = camera.near;
    u.shadowFar.value = environment.csm.maxFar;
    u.sunShadowMapSize.value.set(environment.csm.shadowMapSize, environment.csm.shadowMapSize);
    environment.csm.getExtendedBreaks(this.sunCsmCascades);
    const maps = [u.sunShadowMap0, u.sunShadowMap1, u.sunShadowMap2];
    const matrices = [u.sunShadowMatrix0, u.sunShadowMatrix1, u.sunShadowMatrix2];
    for (let i = 0; i < CSM_CASCADE_CAPACITY; i++) {
      const light = environment.csm.lights[i];
      const map = enabled && i < count ? light?.shadow.map?.texture : null;
      if (map) {
        map.colorSpace = THREE.NoColorSpace;
        map.generateMipmaps = false;
      }
      maps[i].value = map ?? this.dummyShadowMap;
      if (light) matrices[i].value.copy(light.shadow.matrix);
      if (i === 0 && light) {
        u.sunShadowBias.value = light.shadow.bias;
        u.sunShadowNormalBias.value = light.shadow.normalBias;
        u.sunShadowRadius.value = light.shadow.radius;
      }
    }
  }

  private setUniform(name: string, value: number): void {
    this.reservoirMaterial.uniforms[name].value = value;
    this.shadeMaterial.uniforms[name].value = value;
    this.composeMaterial.uniforms[name].value = value;
    this.sunVisMaterial.uniforms[name].value = value;
  }

  private makeSharedUniforms(): Record<string, THREE.IUniform> {
    return {
      depthMap: { value: null },
      albedoMetalMap: { value: null },
      normalRoughMap: { value: null },
      lightMap: { value: this.lightTexture },
      sdfMap: { value: null },
      planeMap: { value: null },
      triangleMap: { value: null },
      braceMap: { value: null },
      latticeOriginMap: { value: null },
      planeCount: { value: 0 },
      triangleCount: { value: 0 },
      braceCount: { value: 0 },
      lightCount: { value: 0 },
      sdfMaxSteps: { value: 48 },
      sunSdfShadows: { value: 1 },
      diSdfShadows: { value: 1 },
      analyticSdf: { value: 1 },
      analyticBvh: { value: 1 },
      voxelSpheres: { value: 1 },
      sunEnabled: { value: 1 },
      ambientEnabled: { value: 1 },
      giNearAmbient: { value: GI_NEAR_AMBIENT },
      giDistanceAmbient: { value: GI_DISTANCE_AMBIENT },
      sdfOrigin: { value: new THREE.Vector3() },
      sdfExtent: { value: 24 },
      sdfVoxelSize: { value: 0.25 },
      projInverse: { value: new THREE.Matrix4() },
      viewInverse: { value: new THREE.Matrix4() },
      cameraPos: { value: new THREE.Vector3() },
      sunDirection: { value: SUN_DIRECTION.clone() },
      sunColor: { value: SUN_COLOR.clone().multiplyScalar(SUN_INTENSITY) },
      skyZenith: { value: SKY_ZENITH.clone() },
      skyHorizon: { value: SKY_HORIZON.clone() },
      skyGround: { value: SKY_GROUND.clone() },
    };
  }

  private makeReservoirTarget(): THREE.WebGLRenderTarget {
    const target = new THREE.WebGLRenderTarget(1, 1, {
      count: 4,
      type: THREE.HalfFloatType,
      minFilter: THREE.NearestFilter,
      magFilter: THREE.NearestFilter,
      depthBuffer: false,
    });
    for (const texture of target.textures) texture.colorSpace = THREE.NoColorSpace;
    return target;
  }

  private makeNoisyTarget(): THREE.WebGLRenderTarget {
    const target = new THREE.WebGLRenderTarget(1, 1, {
      count: 2,
      type: THREE.HalfFloatType,
      minFilter: THREE.NearestFilter,
      magFilter: THREE.NearestFilter,
      depthBuffer: false,
      stencilBuffer: false,
    });
    for (const texture of target.textures) texture.colorSpace = THREE.NoColorSpace;
    return target;
  }
}
